import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, isSameDay, isSameMonth, isAfter, isBefore, startOfDay
} from 'date-fns';

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * Bespoke Turf-Green themed calendar grid.
 * Props:
 *  - selected: Date | null
 *  - onSelect: (Date) => void
 *  - minDate: Date (default today)
 *  - maxDate: Date (default today + 30 days)
 */
export function TurfCalendar({ selected, onSelect, minDate, maxDate }) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const min = minDate ? startOfDay(minDate) : today;
  const max = maxDate ? startOfDay(maxDate) : addDays(today, 30);

  const [viewMonth, setViewMonth] = useState(() => startOfMonth(selected || today));

  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days = [];
  let d = gridStart;
  while (d <= gridEnd) { days.push(d); d = addDays(d, 1); }

  const canGoBack = !isBefore(subMonths(monthStart, 1), startOfMonth(min));
  const canGoForward = !isAfter(addMonths(monthStart, 1), startOfMonth(max));

  const isDisabled = (day) => isBefore(day, min) || isAfter(day, max);

  return (
    <div className="p-3 bg-[#0F0F0F] border border-[#1F1F1F] rounded-xl shadow-2xl shadow-black/40 w-[280px]" data-testid="turf-calendar">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <button
          type="button"
          disabled={!canGoBack}
          onClick={() => canGoBack && setViewMonth(subMonths(viewMonth, 1))}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#16A34A]/10 hover:text-[#16A34A] disabled:opacity-25 disabled:cursor-not-allowed transition-all"
          data-testid="cal-prev"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-[13px] font-semibold font-heading tracking-tight">
          {format(viewMonth, 'MMMM yyyy')}
        </span>
        <button
          type="button"
          disabled={!canGoForward}
          onClick={() => canGoForward && setViewMonth(addMonths(viewMonth, 1))}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#16A34A]/10 hover:text-[#16A34A] disabled:opacity-25 disabled:cursor-not-allowed transition-all"
          data-testid="cal-next"
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 gap-1 mb-1.5">
        {DAYS.map((dl, i) => (
          <div key={i} className="text-center text-[10px] uppercase tracking-wider text-neutral-600 font-body py-1">{dl}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, i) => {
          const inMonth = isSameMonth(day, viewMonth);
          const disabled = isDisabled(day);
          const isSel = selected && isSameDay(day, selected);
          const isToday = isSameDay(day, today);

          let cls = 'h-8 w-full flex items-center justify-center text-[12px] rounded-lg transition-all duration-150 select-none ';
          if (disabled) {
            cls += 'text-neutral-700 cursor-not-allowed';
          } else if (isSel) {
            cls += 'bg-[#16A34A] text-white font-semibold scale-[1.02]';
          } else if (isToday) {
            cls += 'border border-[#16A34A]/40 text-[#16A34A] hover:bg-[#16A34A]/10 cursor-pointer hover:scale-[1.05]';
          } else if (inMonth) {
            cls += 'text-neutral-300 hover:bg-[#16A34A]/10 hover:text-[#16A34A] cursor-pointer hover:scale-[1.05]';
          } else {
            cls += 'text-neutral-700 hover:bg-[#1A1A1A] cursor-pointer';
          }

          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onSelect && onSelect(day)}
              className={cls}
              data-testid={`cal-day-${format(day, 'yyyy-MM-dd')}`}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="mt-3 pt-3 border-t border-[#1A1A1A] flex items-center justify-between text-[10px] font-body">
        <div className="flex items-center gap-1.5 text-neutral-600">
          <span className="w-2 h-2 rounded-sm bg-[#16A34A]"></span>
          Selected
        </div>
        <span className="text-neutral-700">Bookable up to {format(max, 'dd MMM')}</span>
      </div>
    </div>
  );
}

export default TurfCalendar;
